import { usePageState } from "@/navigation/NavigationProvider";
import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link, Navigate } from "react-router-dom";
import {
  BookOpenText,
  BrainCircuit,
  Compass,
  FlaskConical,
  HelpCircle,
  Lightbulb,
  Link2,
  Plus,
  Search,
  Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/auth/AuthContext";
import { OwnerLabConnectionDialog } from "@/components/OwnerLabConnectionDialog";
import { OwnerLabEditorDialog } from "@/components/OwnerLabEditorDialog";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/owner-lab`;

const views = [
  ["overview", "Lab overview", FlaskConical],
  ["explorations", "Explorations", Compass],
  ["knowledge", "Knowledge", BookOpenText],
  ["ideas", "Ideas", Lightbulb],
  ["questions", "Questions", HelpCircle],
  ["connections", "Connections", Link2],
];

const endpointBySection = {
  explorations: "explorations",
  knowledge: "knowledge",
  ideas: "ideas",
  questions: "questions",
};

const displayName = (section) =>
  ({
    explorations: "Exploration",
    knowledge: "Knowledge entry",
    ideas: "Idea",
    questions: "Question",
  })[section];

export default function OwnerLabPage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [view, setView] = usePageState("view", "overview");
  const [query, setQuery] = usePageState("query", "");
  const [editor, setEditor] = useState({ open: false, section: "knowledge", item: null });
  const [connectionEditor, setConnectionEditor] = useState({ open: false, item: null });

  const load = useCallback(async () => {
    const response = await axios.get(`${API}/bootstrap`);
    setData(response.data);
  }, []);

  useEffect(() => {
    if (user?.role === "owner_admin") {
      load().catch((error) => toast.error(error.response?.data?.detail || "Owner Lab could not be loaded."));
    }
  }, [load, user?.role]);

  if (user && user.role !== "owner_admin") {
    return <Navigate replace to="/dashboard" />;
  }

  if (!data) {
    return <div className="owner-lab-loading" data-testid="owner-lab-loading">Opening Owner Lab…</div>;
  }

  const openEditor = (section, item = null) => setEditor({ open: true, section, item });
  const closeEditor = (open) => {
    if (!open) setEditor((current) => ({ ...current, open: false, item: null }));
  };

  const saveEntity = async (section, itemId, payload) => {
    try {
      const endpoint = endpointBySection[section];
      if (itemId) {
        await axios.patch(`${API}/${endpoint}/${itemId}`, payload);
        toast.success(`${displayName(section)} updated.`);
      } else {
        await axios.post(`${API}/${endpoint}`, payload);
        toast.success(`${displayName(section)} added.`);
      }
      setEditor((current) => ({ ...current, open: false, item: null }));
      await load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Owner Lab item could not be saved.");
      throw error;
    }
  };

  const deleteEntity = async (section, itemId) => {
    try {
      await axios.delete(`${API}/${endpointBySection[section]}/${itemId}`);
      toast.success(`${displayName(section)} deleted.`);
      setEditor((current) => ({ ...current, open: false, item: null }));
      await load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Owner Lab item could not be deleted.");
      throw error;
    }
  };

  const saveConnection = async (connectionId, payload) => {
    try {
      if (connectionId) {
        await axios.patch(`${API}/connections/${connectionId}`, payload);
        toast.success("Connection updated.");
      } else {
        await axios.post(`${API}/connections`, payload);
        toast.success("Connection created.");
      }
      setConnectionEditor({ open: false, item: null });
      await load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Connection could not be saved.");
      throw error;
    }
  };

  const deleteConnection = async (connectionId) => {
    try {
      await axios.delete(`${API}/connections/${connectionId}`);
      toast.success("Connection deleted.");
      setConnectionEditor({ open: false, item: null });
      await load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Connection could not be deleted.");
      throw error;
    }
  };

  const entities = {
    exploration: data.explorations,
    knowledge: data.knowledge_entries,
    idea: data.ideas,
    question: data.questions,
  };
  let workspaceContent;
  if (view === "overview") {
    workspaceContent = (
      <Overview
        data={data}
        onOpen={openEditor}
        onOpenConnection={() => setConnectionEditor({ open: true, item: null })}
      />
    );
  } else if (view === "connections") {
    workspaceContent = (
      <ConnectionsView
        connections={data.connections}
        entities={entities}
        onCreate={() => setConnectionEditor({ open: true, item: null })}
        onEdit={(item) => setConnectionEditor({ open: true, item })}
      />
    );
  } else {
    workspaceContent = (
      <EntityView
        items={data[view === "knowledge" ? "knowledge_entries" : view]}
        onCreate={() => openEditor(view)}
        onEdit={(item) => openEditor(view, item)}
        query={query}
        section={view}
      />
    );
  }

  return (
    <main className="owner-lab-shell" data-testid="owner-lab-workspace">
      <aside className="owner-lab-sidebar" data-testid="owner-lab-sidebar">
        <div className="owner-lab-brand" data-testid="owner-lab-brand">
          <FlaskConical size={19} />
          <div><strong>Owner Lab</strong><span>Private thinking space</span></div>
        </div>
        <nav aria-label="Owner Lab sections" className="owner-lab-nav">
          {views.map(([value, label, Icon]) => (
            <button
              className={view === value ? "is-active" : ""}
              data-testid={`owner-lab-view-${value}`}
              key={value}
              onClick={() => setView(value)}
              type="button"
            >
              <Icon size={16} />
              <span>{label}</span>
            </button>
          ))}
        </nav>
        <div className="owner-lab-private-note" data-testid="owner-lab-private-note">
          <Sparkles size={16} />
          <span>Private to you. Only you can see your Lab. It has no operational side effects.</span>
        </div>
        <Link className="owner-lab-return-link" data-testid="owner-lab-return-to-operations" to="/dashboard">
          Return to Operations
        </Link>
      </aside>
      <section className="owner-lab-main">
        <header className="owner-lab-header">
          <div>
            <p>PRIVATE OWNER WORKSPACE</p>
            <h1>{view === "overview" ? "Make room for what you are becoming." : views.find(([value]) => value === view)?.[1]}</h1>
          </div>
          <div className="owner-lab-header-actions">
            <label className="owner-lab-search" htmlFor="owner-lab-search-input">
              <Search size={16} />
              <input
                data-testid="owner-lab-search-input"
                id="owner-lab-search-input"
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search your Lab"
                value={query}
              />
            </label>
            <Button data-testid="owner-lab-quick-capture" onClick={() => openEditor("knowledge")} type="button">
              <Plus size={16} />
              Capture
            </Button>
          </div>
        </header>
        {workspaceContent}
      </section>
      <OwnerLabEditorDialog
        explorations={data.explorations}
        item={editor.item}
        onDelete={deleteEntity}
        onOpenChange={closeEditor}
        onSave={saveEntity}
        open={editor.open}
        section={editor.section}
      />
      <OwnerLabConnectionDialog
        connection={connectionEditor.item}
        entities={entities}
        onDelete={deleteConnection}
        onOpenChange={(open) => !open && setConnectionEditor({ open: false, item: null })}
        onSave={saveConnection}
        open={connectionEditor.open}
      />

    </main>
  );
}

function Overview({ data, onOpen, onOpenConnection }) {
  const recent = [
    ...data.knowledge_entries.map((item) => ({ ...item, section: "knowledge" })),
    ...data.ideas.map((item) => ({ ...item, section: "ideas" })),
    ...data.questions.map((item) => ({ ...item, section: "questions" })),
  ]
    .sort((left, right) => (right.updated_at || right.created_at).localeCompare(left.updated_at || left.created_at))
    .slice(0, 6);

  return (
    <div className="owner-lab-overview" data-testid="owner-lab-overview">
      <section className="owner-lab-intention">
        <div>
          <span>YOUR LONG-TERM THINKING SYSTEM</span>
          <h2>Follow the threads that keep pulling at you.</h2>
          <p>Capture a lesson from a book, a couponing observation, a restaurant question, or a wild idea. The Lab keeps the raw material ready for future connections.</p>
        </div>
        <div className="owner-lab-intention-actions">
          <Button data-testid="owner-lab-create-exploration" onClick={() => onOpen("explorations")} type="button"><Compass size={16} />New exploration</Button>
          <Button data-testid="owner-lab-create-connection" onClick={onOpenConnection} type="button" variant="outline"><Link2 size={16} />Connect threads</Button>
        </div>
      </section>
      <section className="owner-lab-metrics" data-testid="owner-lab-counts">
        <Metric label="Explorations" value={data.counts.explorations} icon={Compass} />
        <Metric label="Knowledge" value={data.counts.knowledge} icon={BookOpenText} />
        <Metric label="Ideas" value={data.counts.ideas} icon={Lightbulb} />
        <Metric label="Questions" value={data.counts.questions} icon={HelpCircle} />
        <Metric label="Connections" value={data.counts.connections} icon={Link2} />
      </section>
      <section className="owner-lab-recent" data-testid="owner-lab-recent">
        <div className="owner-lab-section-heading"><div><span>RECENTLY ADDED</span><h2>Thinking in motion</h2></div><BrainCircuit size={21} /></div>
        {recent.length ? <div className="owner-lab-card-grid">{recent.map((item) => <article key={`${item.section}-${item.id}`}><small>{item.section}</small><h3>{item.title}</h3><p>{item.content || item.description || item.context || "No additional notes yet."}</p><TagRow tags={item.tags} /></article>)}</div> : <EmptyLabState onOpen={onOpen} />}
      </section>
    </div>
  );
}

function Metric({ icon: Icon, label, value }) {
  return <article data-testid={`owner-lab-count-${label.toLowerCase()}`}><Icon size={18} /><strong>{value}</strong><span>{label}</span></article>;
}

function EntityView({ items, onCreate, onEdit, query, section }) {
  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return items;
    return items.filter((item) => [item.title, item.content, item.description, item.context, ...(item.tags || [])].filter(Boolean).join(" ").toLowerCase().includes(needle));
  }, [items, query]);
  return (
    <section className="owner-lab-entity-view" data-testid={`owner-lab-${section}-view`}>
      <div className="owner-lab-section-heading"><div><span>PRIVATE LIBRARY</span><h2>{items.length} {items.length === 1 ? displayName(section) : `${displayName(section)}s`}</h2></div><Button data-testid={`owner-lab-create-${section}`} onClick={onCreate} type="button"><Plus size={16} />New {displayName(section)}</Button></div>
      {filtered.length ? <div className="owner-lab-entity-list">{filtered.map((item) => <button data-testid={`owner-lab-edit-${section}-${item.id}`} key={item.id} onClick={() => onEdit(item)} type="button"><div><small>{(item.status || item.entry_type || "captured").replaceAll("_", " ")}</small><h3>{item.title}</h3><p>{item.content || item.description || item.context || "No additional notes yet."}</p><TagRow tags={item.tags} /></div><span>{item.next_step || item.source_label || item.answer_summary || "Open"}</span></button>)}</div> : <EmptyLabState onOpen={onCreate} section={section} />}
    </section>
  );
}

function ConnectionsView({ connections, entities, onCreate, onEdit }) {
  const lookup = (type, id) => (entities[type] || []).find((item) => item.id === id)?.title || "Unavailable item";
  return <section className="owner-lab-entity-view" data-testid="owner-lab-connections-view"><div className="owner-lab-section-heading"><div><span>MANUAL CONNECTIONS</span><h2>Relationships you have noticed</h2></div><Button data-testid="owner-lab-create-connection" onClick={onCreate} type="button"><Link2 size={16} />Connect threads</Button></div>{connections.length ? <div className="owner-lab-connection-list">{connections.map((connection) => <button data-testid={`owner-lab-edit-connection-${connection.id}`} key={connection.id} onClick={() => onEdit(connection)} type="button"><span>{lookup(connection.from_type, connection.from_id)}</span><i>{connection.relationship_type.replaceAll("_", " ")}</i><strong>{lookup(connection.to_type, connection.to_id)}</strong><p>{connection.note || "Open connection"}</p></button>)}</div> : <EmptyLabState onOpen={onCreate} />}</section>;
}

function EmptyLabState({ onOpen, section = "knowledge" }) {
  return <div className="owner-lab-empty" data-testid="owner-lab-empty-state"><BrainCircuit size={26} /><strong>Nothing here yet.</strong><p>Start with a note, question, idea, or exploration. Small captures compound over time.</p><Button data-testid="owner-lab-empty-create-button" onClick={() => onOpen(section)} type="button"><Plus size={16} />Add your first thread</Button></div>;
}

function TagRow({ tags = [] }) {
  return tags.length ? <div className="owner-lab-tags">{tags.slice(0, 5).map((tag) => <span key={tag}>{tag}</span>)}</div> : null;
}

